import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Package,
  Sparkles,
  ShieldCheck,
  Truck,
  CalendarDays,
  Tag,
  Layers,
  Image as ImageIcon,
  Edit,
  MapPin,
  DollarSign,
  Users,
  ClipboardList
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { formatCurrency, formatDateTime } from '../../lib/utils';
import { Bundle } from '../../types';
import { ShippingConfiguration } from '../../services/shippingService';

interface BundleDetailsModalProps {
  bundle: Bundle | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (bundle: Bundle) => void;
  shippingConfig?: ShippingConfiguration | null;
}

export function BundleDetailsModal({
  bundle,
  isOpen,
  onClose,
  onEdit,
  shippingConfig
}: BundleDetailsModalProps) {
  if (!bundle) return null;

  const priceEntries = Object.entries(bundle.prices || {});

  const addressPolicyLabels: Record<'fixed' | 'latest' | 'adminProvided', string> = {
    fixed: 'Use checkout shipping address',
    latest: 'Use the most recent saved address',
    adminProvided: 'Use the preset address below'
  };

  const addressPolicyLabel = addressPolicyLabels[(bundle.addressPolicy as 'fixed' | 'latest' | 'adminProvided') || 'fixed'];

  const resolveTierName = (tierId?: string | null) => {
    if (!tierId) return 'Not set';
    const tier = shippingConfig?.tiers?.find((t) => t.id === tierId);
    return tier ? tier.name : tierId;
  };

  const purchaseBadges = [
    bundle.purchaseTypes.oneTime && (
      <Badge key="one-time" variant="outline" className="border-jamaica-green text-jamaica-green flex items-center gap-1">
        <Package className="h-3 w-3" /> One-Time
      </Badge>
    ),
    bundle.purchaseTypes.subscription && (
      <Badge key="subscription" variant="outline" className="border-purple-500 text-purple-600 flex items-center gap-1">
        <Sparkles className="h-3 w-3" /> Subscription
      </Badge>
    )
  ].filter(Boolean);

  const handleEditClick = () => {
    if (onEdit) onEdit(bundle);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
          style={{
            margin: 0,
            padding: '1rem',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.5, bounce: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            <div className="relative bg-gradient-to-r from-jamaica-green to-emerald-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/15 to-transparent" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Package className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      Bundle Details
                    </motion.h2>
                    <motion.p
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/85 text-sm"
                    >
                      {bundle.name}
                    </motion.p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className={`border-white/40 text-white/90`}>
                    ID: {bundle.id}
                  </Badge>
                  {onEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleEditClick}
                      className="text-white hover:bg-white/20 hover:text-white"
                    >
                      <Edit className="h-4 w-4 mr-2" /> Edit
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="text-white hover:bg-white/20 hover:text-white rounded-full h-10 w-10"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-gray-50/60">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    {bundle.heroImage ? (
                      <img
                        src={bundle.heroImage}
                        alt={bundle.name}
                        className="w-full h-56 object-cover"
                      />
                    ) : (
                      <div className="w-full h-56 bg-gray-100 flex items-center justify-center">
                        <ImageIcon className="h-12 w-12 text-gray-300" />
                      </div>
                    )}
                    <div className="p-5 space-y-3">
                      <p className="text-sm text-gray-600">{bundle.description || 'No description provided.'}</p>
                      {bundle.tags && bundle.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {bundle.tags.map((tag) => (
                            <Badge key={tag} variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-100">
                              <Tag className="h-3 w-3 mr-1" /> {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {purchaseBadges.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {purchaseBadges}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-jamaica-green" />
                      <h3 className="text-sm font-semibold text-gray-900">Pricing</h3>
                    </div>
                    <Separator />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {priceEntries.map(([currency, config]) => (
                        <div key={currency} className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="uppercase">{currency}</Badge>
                            <span className="text-xs text-gray-500">({currency.toUpperCase()})</span>
                          </div>
                          <div className="flex flex-col text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-lg p-3">
                            <span>
                              One-Time:{' '}
                              <span className="font-medium">
                                {config.oneTime != null ? formatCurrency(Number(config.oneTime), currency.toUpperCase()) : '—'}
                              </span>
                            </span>
                            <span>
                              Subscription:{' '}
                              <span className="font-medium">
                                {config.subscription != null ? formatCurrency(Number(config.subscription), currency.toUpperCase()) : '—'}
                              </span>
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4 text-jamaica-green" />
                      <h3 className="text-sm font-semibold text-gray-900">Shipping & Billing</h3>
                    </div>
                    <Separator />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                      <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                        <p className="text-xs uppercase tracking-wide text-gray-500">One-Time Tier</p>
                        <p className="font-medium text-gray-900">{resolveTierName(bundle.shippingConfig?.oneTimeTierId)}</p>
                      </div>
                      <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                        <p className="text-xs uppercase tracking-wide text-gray-500">Subscription Tier</p>
                        <p className="font-medium text-gray-900">{resolveTierName(bundle.shippingConfig?.subscriptionTierId)}</p>
                      </div>
                      <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                        <p className="text-xs uppercase tracking-wide text-gray-500">Address Policy</p>
                        <p className="font-medium text-gray-900 capitalize flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-jamaica-green" />
                          {addressPolicyLabel}
                        </p>
                      </div>
                      {bundle.addressPolicy === 'adminProvided' && bundle.defaultAddress && (
                        <div className="md:col-span-2 bg-white border border-purple-100 rounded-lg p-4">
                          <p className="text-xs uppercase tracking-wide text-purple-600 font-semibold">Default Fulfillment Address</p>
                          <div className="mt-2 text-sm text-gray-800">
                            <p className="font-semibold">{bundle.defaultAddress.name}</p>
                            {bundle.defaultAddress.email && <p className="text-xs text-gray-500">{bundle.defaultAddress.email}</p>}
                            {bundle.defaultAddress.phone && <p className="text-xs text-gray-500">{bundle.defaultAddress.phone}</p>}
                            <div className="mt-2 text-gray-700 space-y-0.5">
                              <p>{bundle.defaultAddress.address?.line1}</p>
                              {bundle.defaultAddress.address?.line2 && <p>{bundle.defaultAddress.address.line2}</p>}
                              <p>
                                {bundle.defaultAddress.address?.city}, {bundle.defaultAddress.address?.state}{' '}
                                {bundle.defaultAddress.address?.postal_code}
                              </p>
                              <p>{bundle.defaultAddress.address?.country}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <ClipboardList className="h-4 w-4 text-jamaica-green" />
                      <h3 className="text-sm font-semibold text-gray-900">Included Items</h3>
                    </div>
                    <Separator />
                    <div className="space-y-3">
                      {bundle.items && bundle.items.length > 0 ? (
                        bundle.items.map((item) => (
                          <div key={item.productId} className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg p-3 bg-gray-50">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="h-12 w-12 rounded-md overflow-hidden bg-white border border-gray-200 flex-shrink-0">
                                {item.image ? (
                                  <img src={item.image} alt={item.productName || 'Bundle Item'} className="h-full w-full object-cover" />
                                ) : (
                                  <div className="h-full w-full flex items-center justify-center">
                                    <ImageIcon className="h-6 w-6 text-gray-300" />
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col min-w-0">
                                <span className="text-sm font-medium text-gray-900 truncate">{item.productName || 'Bundle Item'}</span>
                                <span className="text-xs text-gray-500">Product ID: {item.productId}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-600">
                              <Badge variant="outline" className="bg-white text-gray-700 border-gray-200">
                                Qty: {item.quantity}
                              </Badge>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-gray-500">No bundle items configured.</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-emerald-600" />
                      <h3 className="text-sm font-semibold text-gray-900">Status</h3>
                    </div>
                    <Separator />
                    <div className="space-y-2 text-sm text-gray-700">
                      <div className="flex items-center justify-between">
                        <span>Published Status</span>
                        <Badge variant={bundle.published ? 'default' : 'secondary'} className={bundle.published ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-600'}>
                          {bundle.published ? 'Published' : 'Draft'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Disabled</span>
                        <Badge variant="secondary" className={bundle.disabled ? 'bg-red-100 text-red-700 border-red-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}>
                          {bundle.disabled ? 'Disabled' : 'Active'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Stripe Sync</span>
                        <Badge variant="secondary" className={(bundle.stripe?.needsStripeSync ?? true) ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}>
                          {(bundle.stripe?.needsStripeSync ?? true) ? 'Needs Sync' : 'Synced'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Active Subscribers</span>
                        <span className="font-semibold text-gray-900 flex items-center gap-1">
                          <Users className="h-4 w-4 text-emerald-600" />
                          {bundle.activeSubscriptionCount ?? 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-jamaica-green" />
                      <h3 className="text-sm font-semibold text-gray-900">Timeline</h3>
                    </div>
                    <Separator />
                    <div className="space-y-2 text-sm text-gray-700">
                      <div className="flex flex-col">
                        <span className="text-xs uppercase tracking-wide text-gray-500">Created</span>
                        <span className="font-medium text-gray-900">{bundle.createdAt ? formatDateTime(bundle.createdAt) : 'N/A'}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs uppercase tracking-wide text-gray-500">Last Updated</span>
                        <span className="font-medium text-gray-900">{bundle.updatedAt ? formatDateTime(bundle.updatedAt) : 'N/A'}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs uppercase tracking-wide text-gray-500">Visible From</span>
                        <span className="font-medium text-gray-900">{bundle.visibleFrom ? formatDateTime(bundle.visibleFrom) : 'Immediate'}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs uppercase tracking-wide text-gray-500">Visible To</span>
                        <span className="font-medium text-gray-900">{bundle.visibleTo ? formatDateTime(bundle.visibleTo) : 'No end date'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-jamaica-green" />
                      <h3 className="text-sm font-semibold text-gray-900">Metadata</h3>
                    </div>
                    <Separator />
                    <div className="space-y-2 text-sm text-gray-700">
                      <div className="flex items-center justify-between">
                        <span>Slug</span>
                        <Badge variant="outline" className="font-mono text-xs">{bundle.slug}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Category</span>
                        <span className="font-medium text-gray-900">{bundle.category || 'Uncategorized'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Total Bundle Items</span>
                        <span className="font-medium text-gray-900">{bundle.items?.length || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 bg-white border-t border-gray-100 flex items-center justify-end gap-3 flex-shrink-0">
              {onEdit && (
                <Button variant="outline" onClick={handleEditClick} className="flex items-center gap-2">
                  <Edit className="h-4 w-4" /> Edit Bundle
                </Button>
              )}
              <Button onClick={onClose} className="bg-jamaica-green hover:bg-jamaica-green/90 text-white">
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
